/**
 * HR module — redesigned (iter 10) with sub-tabs.
 *
 * Sub-tabs:
 *  01 Necesidades — narrative description of staffing needs and culture.
 *  02 Equipo — editable role cards (title, responsibilities, salary range, headcount).
 *  03 Plantilla mensual — auto-computed monthly cost view + simple org-chart
 *     visualisation grouped by area (Liderazgo · Producto/Operaciones · Comercial/Mkt · Soporte).
 *
 * No new data model: works with the existing `{needs_description, roles}` shape.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  Users,
  ClipboardList,
  Network,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
  SubTabs,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";

const empty = { needs_description: "", roles: [] };

// Best-effort categorisation by role title (Spanish + English keywords).
function classifyRole(title) {
  const t = (title || "").toLowerCase();
  if (/(ceo|coo|cfo|cto|founder|director general|director ejecutiv|presiden)/.test(t)) return "leadership";
  if (/(market|sales|comercial|growth|account|bdr|sdr|customer success)/.test(t)) return "go-to-market";
  if (/(product|ux|ui|designer|engineer|developer|tech|cto|ingenier|desarroll|ops|operac|project manager)/.test(t)) return "product-ops";
  return "support";
}

const AREAS = [
  { key: "leadership",   label_es: "Liderazgo",          label_en: "Leadership",         color: "#2563EB" },
  { key: "product-ops",  label_es: "Producto / Operaciones", label_en: "Product / Ops",  color: "#3A5A6B" },
  { key: "go-to-market", label_es: "Comercial / Marketing",  label_en: "Go-to-market",  color: "#B89154" },
  { key: "support",      label_es: "Soporte",            label_en: "Support",            color: "#5C5B5A" },
];

function parseMonthlyCost(salaryRange, headcount = 1) {
  // Expect annual ranges like "35.000 - 45.000" or "€80k-€120k". Take midpoint, /12, * headcount.
  const s = String(salaryRange || "").replace(/[€$£\s]/g, "").replace(/\./g, "").toLowerCase();
  const parts = s.split(/[-–—]/);
  const nums = parts
    .map((p) => {
      const m = p.match(/[\d]+(?:\.\d+)?/);
      if (!m) return null;
      let n = parseFloat(m[0]);
      if (/k/.test(p)) n *= 1000;
      return n;
    })
    .filter((n) => n != null);
  if (nums.length === 0) return 0;
  const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
  return Math.round((avg / 12) * (headcount || 1));
}

export default function HRModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [tab, setTab] = useState("needs");

  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const add = () =>
    setV((p) => ({
      ...p,
      roles: [...(p.roles || []), { title: "", responsibilities: "", salary_range: "", headcount: 1 }],
    }));
  const update = (i, k, val) =>
    setV((p) => ({ ...p, roles: p.roles.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const remove = (i) =>
    setV((p) => ({ ...p, roles: p.roles.filter((_, idx) => idx !== i) }));

  const totalHeadcount = (v.roles || []).reduce((s, r) => s + (parseInt(r.headcount) || 0), 0);

  const grouped = useMemo(() => {
    const out = { leadership: [], "product-ops": [], "go-to-market": [], support: [] };
    (v.roles || []).forEach((r) => {
      const area = classifyRole(r.title);
      out[area].push(r);
    });
    return out;
  }, [v.roles]);

  const totalMonthlyCost = useMemo(
    () => (v.roles || []).reduce((s, r) => s + parseMonthlyCost(r.salary_range, parseInt(r.headcount) || 1), 0),
    [v.roles]
  );

  const suggestions = lang === "es"
    ? [
        "Lista los perfiles clave necesarios para el primer año",
        "Sugiere rangos salariales en EUR para cada rol",
        "Define responsabilidades del CEO y COO",
        "Estima coste mensual de equipo Year 1",
      ]
    : [
        "List the key roles needed in year 1",
        "Suggest EUR salary ranges for each role",
        "Define CEO and COO responsibilities",
        "Estimate monthly team cost Year 1",
      ];

  const tabs = [
    { key: "needs",   label: lang === "es" ? "Necesidades"        : "Staffing needs", icon: ClipboardList },
    { key: "team",    label: lang === "es" ? "Equipo"             : "Team",           icon: Users },
    { key: "org",     label: lang === "es" ? "Plantilla & Org"    : "Headcount & Org", icon: Network },
  ];

  return (
    <>
      <SectionCard
        title={t("module.hr")}
        subtitle={lang === "es" ? "Equipo, perfiles, responsabilidades y plantilla mensual." : "Team, roles, responsibilities and monthly headcount."}
        action={
          <div className="flex items-center gap-3">
            <AIBadge />
            <AIAssistant module="hr" context={{ project: project?.name, needs: v.needs_description }} suggestions={suggestions} />
          </div>
        }
        testId="hr-section"
      >
        <SubTabs tabs={tabs} active={tab} onChange={setTab} testIdPrefix="hr-subtab" />

        {tab === "needs" && (
          <div data-testid="hr-tab-needs" className="space-y-5">
            <p className="text-sm text-[#5C5B5A] max-w-2xl">
              {lang === "es"
                ? "Describe la cultura, el modelo de talento y las prioridades de contratación del primer año. Esto guiará al asistente IA al sugerir roles."
                : "Describe the culture, talent model and Year 1 hiring priorities. This guides the AI assistant when suggesting roles."}
            </p>
            <div>
              <FieldLabel>{t("hr.needs")}</FieldLabel>
              <TextArea value={v.needs_description} onChange={(x) => set("needs_description", x)} rows={8} testId="hr-needs-textarea" />
            </div>
          </div>
        )}

        {tab === "team" && (
          <div data-testid="hr-tab-team" className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <Overline>{lang === "es" ? "Roles y responsabilidades" : "Roles and responsibilities"}</Overline>
              <SecondaryButton onClick={add} testId="add-role-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("hr.add_role")}
              </SecondaryButton>
            </div>
            {(v.roles || []).length === 0 ? (
              <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                {lang === "es" ? "Sin roles definidos. Añade el primero." : "No roles yet. Add the first one."}
              </div>
            ) : (
              <div className="space-y-3">
                {v.roles.map((r, i) => (
                  <div key={i} className="border border-[#E8E6E1] rounded-sm p-5 bg-white" data-testid={`role-${i}`}>
                    <div className="grid md:grid-cols-[2fr_1fr_100px] gap-3">
                      <div>
                        <FieldLabel>{t("hr.title")}</FieldLabel>
                        <TextInput value={r.title} onChange={(x) => update(i, "title", x)} testId={`role-${i}-title`} />
                      </div>
                      <div>
                        <FieldLabel hint={lang === "es" ? "Anual EUR" : "Annual EUR"}>{t("hr.salary")}</FieldLabel>
                        <TextInput value={r.salary_range} onChange={(x) => update(i, "salary_range", x)} placeholder="35.000 - 45.000" testId={`role-${i}-salary`} />
                      </div>
                      <div>
                        <FieldLabel>{t("hr.headcount")}</FieldLabel>
                        <TextInput type="number" value={r.headcount} onChange={(x) => update(i, "headcount", parseInt(x) || 0)} testId={`role-${i}-headcount`} />
                      </div>
                    </div>
                    <div className="mt-3">
                      <FieldLabel>{t("hr.responsibilities")}</FieldLabel>
                      <TextArea value={r.responsibilities} onChange={(x) => update(i, "responsibilities", x)} rows={3} testId={`role-${i}-resp`} />
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="text-xs text-[#8A8886] font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                        {lang === "es" ? "Coste mensual estimado" : "Estimated monthly cost"}: €{parseMonthlyCost(r.salary_range, parseInt(r.headcount) || 1).toLocaleString()}
                      </span>
                      <button onClick={() => remove(i)} className="text-[#8A8886] hover:text-[#9E2A2B] text-sm inline-flex items-center gap-1" data-testid={`role-${i}-remove`}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} /> {t("common.delete")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "org" && (
          <div data-testid="hr-tab-org" className="space-y-6">
            {/* Summary KPIs */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="border border-[#E8E6E1] rounded-sm p-5 bg-white">
                <Overline>{lang === "es" ? "Plantilla total" : "Total headcount"}</Overline>
                <div className="mt-2 text-3xl font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{totalHeadcount}</div>
                <p className="text-xs text-[#8A8886] mt-1">{lang === "es" ? "personas" : "people"}</p>
              </div>
              <div className="border border-[#E8E6E1] rounded-sm p-5 bg-white">
                <Overline>{lang === "es" ? "Coste mensual" : "Monthly cost"}</Overline>
                <div className="mt-2 text-3xl font-mono text-[#2563EB]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>€{totalMonthlyCost.toLocaleString()}</div>
                <p className="text-xs text-[#8A8886] mt-1">{lang === "es" ? "estimado · sin SS" : "estimated · pre-SS"}</p>
              </div>
              <div className="border border-[#E8E6E1] rounded-sm p-5 bg-[#F5EFE6]">
                <Overline><span className="text-[#B89154]">{lang === "es" ? "Coste anual + SS (30%)" : "Annual cost + SS (30%)"}</span></Overline>
                <div className="mt-2 text-3xl font-mono text-[#B89154]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>€{Math.round(totalMonthlyCost * 12 * 1.30).toLocaleString()}</div>
                <p className="text-xs text-[#8A8886] mt-1">{lang === "es" ? "incluye SS estimada" : "includes SS estimate"}</p>
              </div>
            </div>

            {/* Org-chart-lite */}
            <div className="border border-[#E8E6E1] rounded-sm bg-white p-6">
              <Overline>{lang === "es" ? "Organigrama por área" : "Org chart by area"}</Overline>
              <p className="text-xs text-[#8A8886] mt-1 mb-5">
                {lang === "es" ? "Categorización automática por título de rol." : "Auto-categorisation by role title."}
              </p>
              <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
                {AREAS.map((area) => {
                  const roles = grouped[area.key] || [];
                  return (
                    <div key={area.key} className="border border-[#E8E6E1] rounded-sm p-4 bg-[#FBFAF7]" data-testid={`org-area-${area.key}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-1 h-5 rounded-sm" style={{ backgroundColor: area.color }} />
                        <Overline><span style={{ color: area.color }}>{lang === "es" ? area.label_es : area.label_en}</span></Overline>
                        <span className="text-xs text-[#8A8886] ml-auto font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                          {roles.reduce((s, r) => s + (parseInt(r.headcount) || 0), 0)}
                        </span>
                      </div>
                      {roles.length === 0 ? (
                        <p className="text-xs text-[#8A8886]">—</p>
                      ) : (
                        <ul className="space-y-2">
                          {roles.map((r, idx) => (
                            <li key={idx} className="text-sm text-[#1A1A1A] border-b border-[#F0EFE9] pb-2 last:border-0 last:pb-0">
                              <div className="font-medium">{r.title || "—"}</div>
                              <div className="text-xs text-[#8A8886] flex justify-between mt-0.5">
                                <span className="font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>×{r.headcount || 1}</span>
                                <span className="font-mono" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{r.salary_range || "—"}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </SectionCard>

    </>
  );
}
